import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import RecipeCard from './RecipeCard';
import FavoriteRecipes from './FavoriteRecipes';
import RecipeSort from './RecipeSort';
import AddToWeeklyMenuDialog from './AddToWeeklyMenuDialog';
import { useRecipes } from '@/hooks/useRecipes';
import { useAuth } from '@/contexts/AuthContext';
import { Recipe, Tag, MealCategory } from '@/types';
import { useRecommendations } from '@/hooks/useRecommendations';
import { useFavorites } from '@/hooks/useFavorites';
import { RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

const FeaturedRecipes = () => {
  const [activeTab, setActiveTab] = useState('todas');
  const [sortBy, setSortBy] = useState('todas');

  // ✅ SOLUCIÓN: Solo una llamada a useFavorites
  const { favoriteIds, toggleFavorite, loading: favoritesLoading } = useFavorites();

  const [addToMenuDialog, setAddToMenuDialog] = useState({
    isOpen: false,
    recipeTitle: '',
    recipeId: 0
  });

  const { recipes, loading, error } = useRecipes();
  const {
    recommendations: smartRecommendations,
    loading: recommendationsLoading,
    error: recommendationsError,
    refetch: refetchRecommendations
  } = useRecommendations();

  const { user, isLoggedIn } = useAuth();

  // Cargar perfil del usuario desde localStorage
  const [userProfile, setUserProfile] = useState<any>(null);

  useEffect(() => {
    const savedProfile = localStorage.getItem('userProfile');
    if (savedProfile) {
      try {
        setUserProfile(JSON.parse(savedProfile));
      } catch (error) {
        console.error('Error parsing user profile:', error);
      }
    }
  }, []);

  // ✅ DEBUG: Agregar esto temporalmente
  useEffect(() => {
    console.log("🔍 FEATURED RECIPES DEBUG:");
    console.log("smartRecommendations:", smartRecommendations);
    console.log("recommendationsLoading:", recommendationsLoading);
    console.log("recommendationsError:", recommendationsError);
    console.log("Number of recommendations:", smartRecommendations?.length || 0);
    console.log("isLoggedIn:", isLoggedIn);
    console.log("user:", user);
  }, [smartRecommendations, recommendationsLoading, recommendationsError, isLoggedIn, user]);

  const handleAddToWeeklyMenu = (recipeId: number, recipeTitle: string) => {
    setAddToMenuDialog({
      isOpen: true,
      recipeTitle,
      recipeId
    });
  };

  // ✅ CORREGIDO: Función para reintentar recomendaciones
  const handleRetryRecommendations = () => {
    refetchRecommendations(); // Pasar el límite como número
  };

  // Función simplificada de filtrado y ordenamiento
  const filterAndSortRecipes = (recipeList: Recipe[]) => {
    let filtered = [...recipeList];

    // Filtrar por categoría o tag
    if (sortBy !== 'todas') {
      const categoryMap: { [key: string]: MealCategory; } = {
        'desayuno': MealCategory.DESAYUNO,
        'comida': MealCategory.COMIDA,
        'cena': MealCategory.CENA,
        'snack': MealCategory.SNACK
      };

      const tagMap: { [key: string]: Tag; } = {
        'bajo-carbos': Tag.BAJO_EN_CARBOS,
        'alto-proteina': Tag.ALTA_PROTEINA,
        'vegetariano': Tag.VEGETARIANA,
        'vegano': Tag.VEGANA,
        'gluten-free': Tag.GLUTEN_FREE,
        'dairy-free': Tag.SIN_LACTOSA,
        'keto': Tag.KETO,
        'bajo-calorias': Tag.BAJO_EN_CALORIAS
      };

      const targetCategory = categoryMap[sortBy];
      const targetTag = tagMap[sortBy];

      if (targetCategory) {
        filtered = filtered.filter(recipe => recipe.category === targetCategory);
      } else if (targetTag) {
        filtered = filtered.filter(recipe => recipe.tags.includes(targetTag));
      }
    }

    return filtered;
  };

  const filteredRecipes = filterAndSortRecipes(recipes);
  const filteredRecommendations = filterAndSortRecipes(smartRecommendations);

  const getRecommendationHeader = (): { header: string; subtext: string; } => {
    if (!userProfile) {
      return {
        header: "Recetas Recomendadas para Ti",
        subtext: "Basado en tus preferencias guardadas"
      };
    }

    const { personalInfo, allergies, dislikes } = userProfile;

    let header = "Recetas Personalizadas";
    let subtext = "";

    if (personalInfo?.weightGoal) {
      switch (personalInfo.weightGoal) {
        case 'lose':
          header = "💪 Recetas para Perder Peso";
          subtext = `Bajas en calorías (meta: ${userProfile.recommendedCalories} cal/día)`;
          break;
        case 'gain':
          header = "🔥 Recetas para Ganar Masa Muscular";
          subtext = `Altas en proteína y calorías (meta: ${userProfile.recommendedCalories} cal/día)`;
          break;
        case 'maintain':
          header = "⚖️ Recetas para Mantener tu Peso";
          subtext = `Balance perfecto (meta: ${userProfile.recommendedCalories} cal/día)`;
          break;
      }
    }

    if (allergies?.length > 0) {
      subtext += subtext ? ` • Sin: ${allergies.join(', ')}` : `Evitando: ${allergies.join(', ')}`;
    }

    return { header, subtext };
  };

  const getPersonalizedBadges = () => {
    if (!userProfile) return [];

    const badges = [];
    const { personalInfo, allergies, dislikes } = userProfile;

    if (personalInfo?.weightGoal) {
      badges.push({
        text: personalInfo.weightGoal === 'lose' ? '🥗 Bajar Peso' :
          personalInfo.weightGoal === 'gain' ? '💪 Subir Peso' : '⚖️ Mantener Peso',
        color: 'bg-blue-100 text-blue-800'
      });
    }

    if (allergies?.length > 0) {
      badges.push({
        text: `⚠️ ${allergies.length} alergia${allergies.length > 1 ? 's' : ''}`,
        color: 'bg-red-100 text-red-800'
      });
    }

    if (dislikes?.length > 0) {
      badges.push({
        text: `👎 ${dislikes.length} evitado${dislikes.length > 1 ? 's' : ''}`,
        color: 'bg-orange-100 text-orange-800'
      });
    }

    return badges;
  };

  const renderRecommendationBadges = () => {
    const badges = getPersonalizedBadges();

    if (badges.length === 0) return null;

    return (
      <div className="flex flex-wrap gap-2 mb-4">
        {badges.map((badge, index) => (
          <span
            key={index}
            className={`px-3 py-1 rounded-full text-xs font-medium ${badge.color}`}
          >
            {badge.text}
          </span>
        ))}
      </div>
    );
  };

  // ✅ NUEVA FUNCIÓN: Renderizar recetas recomendadas corregida
  const renderRecommendedRecipes = () => {
    if (recommendationsLoading) {
      return (
        <div className="flex justify-center items-center h-32">
          <div className="text-lg text-gray-600">Buscando las mejores recetas para ti...</div>
        </div>
      );
    }

    if (recommendationsError) {
      return (
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <span className="text-2xl">❌</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Error cargando recomendaciones
          </h3>
          <p className="text-gray-600 mb-4">{recommendationsError}</p>
          <button
            onClick={handleRetryRecommendations}
            className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Reintentar
          </button>
        </div>
      );
    }

    if (filteredRecommendations.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
            <span className="text-2xl">🔍</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No encontramos recetas que coincidan perfectamente
          </h3>
          <p className="text-gray-600 mb-4 max-w-md mx-auto">
            Intenta ajustar tus preferencias en el perfil o explora todas las recetas disponibles.
          </p>
          <button
            onClick={() => setActiveTab('todas')}
            className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Ver Todas las Recetas
          </button>
        </div>
      );
    }

    return (
      <>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredRecommendations.slice(0, 12).map((recipe) => (
            <RecipeCard
              key={recipe.id}
              {...recipe}
              onAddToWeeklyMenu={handleAddToWeeklyMenu}
              isFavorite={favoriteIds.includes(recipe.id)}  // ✅ AGREGADO - CRÍTICO
              toggleFavorite={toggleFavorite}               // ✅ AGREGADO - CRÍTICO
              showRecommendationBadge={true}
            />
          ))}
        </div>

        <div className="text-center mt-8">
          <p className="text-gray-600 text-sm">
            Mostrando {Math.min(filteredRecommendations.length, 12)} de {filteredRecommendations.length} recetas recomendadas
          </p>
        </div>
      </>
    );
  };

  // Mostrar estados de carga y error
  if (loading) {
    return (
      <div className="py-8">
        <div className="flex justify-center items-center h-32">
          <div className="text-lg text-gray-600">Cargando recetas...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8">
        <div className="flex justify-center items-center h-32">
          <div className="text-lg text-red-600">Error: {error}</div>
        </div>
      </div>
    );
  }

  const { header: recommendationHeader, subtext: recommendationSubtext } = getRecommendationHeader();

  return (
    <div className="py-8">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <TabsList className="grid w-full sm:w-auto grid-cols-2 lg:grid-cols-4">
            <TabsTrigger value="todas">Todas</TabsTrigger>
            <TabsTrigger value="favoritas">Favoritas</TabsTrigger>
            {isLoggedIn && (
              <TabsTrigger value="recomendadas">Para Ti</TabsTrigger>
            )}
          </TabsList>

          <div className="flex gap-2">
            <RecipeSort sortBy={sortBy} onSortChange={setSortBy} />
          </div>
        </div>

        <TabsContent value="todas" className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold mb-4">
              {isLoggedIn ? 'Todas las Recetas Disponibles' : 'Recetas Públicas Mexicanas'}
            </h2>
            <p className="text-gray-600 mb-4">
              {isLoggedIn
                ? 'Recetas públicas y tus recetas privadas'
                : 'Regístrate para crear tu menú semanal y guardar tus recetas favoritas'
              }
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredRecipes.map((recipe) => (
                <RecipeCard
                  key={recipe.id}
                  {...recipe}
                  onAddToWeeklyMenu={handleAddToWeeklyMenu}
                  isFavorite={favoriteIds.includes(recipe.id)}
                  toggleFavorite={toggleFavorite}
                />
              ))}
            </div>
            {filteredRecipes.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No se encontraron recetas con los filtros seleccionados.
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="recomendadas" className="space-y-6">
          <div>
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg mb-6 border border-blue-200">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-bold mb-2 text-gray-900">
                    {recommendationHeader}
                  </h2>
                  {recommendationSubtext && (
                    <p className="text-gray-700 mb-3">{recommendationSubtext}</p>
                  )}
                </div>
                <Button
                  onClick={() => refetchRecommendations()}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Actualizar
                </Button>
              </div>
              {renderRecommendationBadges()}
              <p className="text-sm text-gray-600">
                💡 Estas recetas fueron seleccionadas especialmente para ti basándonos en tus metas, alergias y preferencias.
              </p>
            </div>

            {/* ✅ REEMPLAZADO: Usar la función corregida */}
            {renderRecommendedRecipes()}
          </div>
        </TabsContent>

        <TabsContent value="favoritas">
          <FavoriteRecipes onAddToWeeklyMenu={handleAddToWeeklyMenu} />
        </TabsContent>
      </Tabs>

      <AddToWeeklyMenuDialog
        isOpen={addToMenuDialog.isOpen}
        onClose={() => setAddToMenuDialog({ isOpen: false, recipeTitle: '', recipeId: 0 })}
        recipeTitle={addToMenuDialog.recipeTitle}
        recipeId={addToMenuDialog.recipeId}
      />
    </div>
  );
};

export default FeaturedRecipes;